import {
  type ConflictKmatDTO,
  type KmatDTO,
  type LinearConfigurationDTO,
  type LinearCsticDTO,
  type LinearCsticValueDTO,
} from "~/features/configurator/Linear/dtos";
import {
  type ConflictKmat,
  type Kmat,
  type LinearConfiguration,
  type LinearCstic,
  type LinearCsticValue,
} from "~/features/configurator/Linear/models";
import {
  CSTIC_SUPPORT_KIT_NAME,
  DISCLAIMER_RAIL_ABOVE,
  DISCLAIMER_RAIL_BELOW,
  FAKE_CSTIC_CONFIGURATION_VERSION,
  FAKE_CSTIC_UNIT_LENGTH,
  GET_ORDER_DESIGNTION_TEXT,
  GREASING_KEY_DEFAULT_KMAT,
  GREASING_OPTION_KEY_DEFAULT_KMAT,
  GREASING_OPTION_STANDARD_VALUE,
  GREASING_STANDARD_VALUE,
  ORDER_DESIGNATION_HTML_IDS,
} from "~/features/configurator/Linear/constants";
import { groupByFn, toMapFn } from "~/common/utils/reducers";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { CARRIAGE_DETAILS_INFO_MSG } from "~/features/configurator/Carriage/carriageConfiguratorConstants";
import { userIsPublic, userIsRegistered } from "~/domains/user/utils/roles";
import {
  resolvePotentiallyExternalLabel,
  type TransformationContext,
  transformToExternalUrl,
} from "~/features/configurator/common/translationUtils";
import {
  buildAdvancedTranslationsForUnit,
  RESET_STANDARD_AND_LENGTH,
} from "~/features/configurator/Linear/advancedTranslationsTransformer";
import { getConfiguratorDefinition } from "~/bridges/configurator/configuratorRegistry";
import {
  chamfer,
  radialThread,
  SHAFT_LENGTH_INCH_MAX,
  SHAFT_LENGTH_INCH_MIN,
  SHAFT_LENGTH_METRIC_MAX,
  SHAFT_LENGTH_METRIC_MIN,
} from "~/features/configurator/Shaft/inch/inchCsticConstants";
import { REFERENCE_MATERIAL_CSTIC } from "~/domains/configurator/constants";
import type { TranslationModel } from "~/common/types/TranslationModel";
import { pushStandardValueIfMissing } from "~/features/configurator/common/v2/helper";
import {
  RESET_SELECTION,
  RESET_SELECTION_AND_LENGTH,
  RESET_STANDARD,
  resolveInternalAdvancedTranslation,
} from "~/features/configurator/common/advancedTranslationUtils";
import { applyConfigurationFromDefinition } from "~/features/configurator/common/v2/applyConfigurationFromDefinition";
import { addFakeCstic } from "~/features/configurator/transformerHelper";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import { KmatType } from "~/features/configurator/Linear/kmatType";
import { GuidewayLength } from "../Rail/v2/configuration/classic/RailsCstics";

export function transform(
  dto: LinearConfigurationDTO,
  configuratorType: ConfiguratorType,
  csticsToPreserve?: LinearCstic[],
): LinearConfiguration {
  const context: TransformationContext = {
    productCode: dto.productCode,
    configuratorType,
    baseExternalUrl: dto.baseExternalUrl,
    specialTranslations: dto.specialTranslations ?? {},
  };
  const kmats = dto.kmats.filter((kmat) => kmat.cstics.length > 0).map((kmat) => transformKmat(kmat, context));

  if (configuratorType === ConfiguratorType.System) {
    addFakeCstic(kmats, FAKE_CSTIC_CONFIGURATION_VERSION, dto.configVersion ?? "");

    const railKmat = kmats.filter((kmat) => kmat.type === KmatType.SubRail)[0];
    const value = railKmat?.cstics?.[GuidewayLength]?.value;

    addFakeCstic(kmats, {
      name: FAKE_CSTIC_UNIT_LENGTH,
      value: value ?? undefined,
      fake: false,
      groupId: "1-089750276000010@10-BEWERTUNG",
      key: FAKE_CSTIC_UNIT_LENGTH,
    });
  }

  const kmatMap = groupByFn(kmats, (kmat) => kmat.type);
  addMandatoryValuesForLubricationOptions(kmatMap, configuratorType);

  const mainKmat = getMainKmat({ kmats: kmatMap });
  csticsToPreserve?.forEach((cstic) => (mainKmat.cstics[cstic.name] = cstic));

  let guidewayKmats: Kmat[];
  if (configuratorType === ConfiguratorType.System) {
    guidewayKmats = kmatMap[KmatType.SubRail];
  } else if (configuratorType === ConfiguratorType.Rail) {
    guidewayKmats = kmatMap[KmatType.Main];
  } else {
    guidewayKmats = [];
  }
  const guidewayPieceLimits = transformGuidewayPieceLimits(dto, guidewayKmats);
  let configuration: LinearConfiguration = {
    ...dto,
    kmats: kmatMap,
    steps: [],
    substeps: {},
    mediaUrl: "",
    description: "",
    designation: dto.designation ?? "-",
    schaefflerId: dto.schaefflerId ?? "",
    isConfigComplete: dto.complete ?? false,
    currentStep: dto.currentStep ?? 1,
    highestVisitedStep: dto.highestVisitedStep ?? 1,
    guidewayPieceLimits,
    specialTranslations: buildTranslationsMap(context),
    advancedSpecialTranslations: buildAdvancedTranslationsMap(context, dto),
    enableCarriageEdit: dto.enableCarriageEdit,
    conflictedCstics: (dto.conflictedCstics || []).map(transformConflictKmat),
    allowedSteps: dto.allowedSteps,
  };

  const configuratorDefinition = getConfiguratorDefinition(configuration.productCode);
  if (configuratorDefinition.modelAndUpdatePreprocessor) {
    configuration = configuratorDefinition.modelAndUpdatePreprocessor(configuration, dto);
  }

  applyConfigurationFromDefinition(configuration, kmatMap, dto.standardValues);

  if (configuratorDefinition.postDefinitionProcessor) {
    configuration = configuratorDefinition.postDefinitionProcessor(configuration, dto);
  }

  return configuration;
}

function transformKmat(dto: KmatDTO, context: TransformationContext): Kmat {
  return {
    ...dto,
    cstics: toMapFn(
      dto.cstics.map((cstic) => transformCstic(cstic, context, dto.parentId)),
      (cstic) => cstic.key,
    ),
  };
}

function transformCstic(dto: LinearCsticDTO, context: TransformationContext, parentGroupId?: string): LinearCstic {
  let label = null;
  let name = null;
  let info = null;

  const idParts = dto.key.split("@");
  const key = idParts.pop()!;
  const groupId = idParts.join("@");

  if (context.configuratorType !== ConfiguratorType.System) {
    // The "KIT" label in configure carriage comes as a special translation for carriage configurator & others,
    // but inside CSTIC_SUPPORT_KIT_NAME for regular linear configurator
    if (dto.name === CSTIC_SUPPORT_KIT_NAME) {
      label = resolveSpecialTranslation("KIT", "name", dto.label, context);
    }
  }

  // Default way of filling out label, name, info
  if (label === null) {
    label = dto.label;
  }
  if (name === null) {
    name = resolveSpecialTranslation(dto.name, "name", dto.name, context);
  }
  if (info === null) {
    info = resolveSpecialTranslation(dto.name, "info", dto.info, context);
  }

  const cstic: LinearCstic = {
    ...dto,
    key,
    groupId,
    parentGroupId,
    values: dto.values.map((value) => transformCsticValue(dto, value, context)),
    description: resolveSpecialTranslation(dto.name, "description", dto.description, context),
    name,
    info,
    label,
    readonly: READ_ONLY_CSTICS.has(key),
    fake: false,
  };
  return cstic;
}

function transformCsticValue(
  csticDTO: LinearCsticDTO,
  valueDTO: LinearCsticValueDTO,
  context: TransformationContext,
): LinearCsticValue {
  const val: LinearCsticValue = {
    ...valueDTO,
    standard: valueDTO.standard ?? valueDTO.defaultValue,
    label: resolveSpecialTranslation(csticDTO.name + "_" + valueDTO.value, "name", valueDTO.label, context),
  };

  return val;
}

function transformConflictKmat(dto: ConflictKmatDTO): ConflictKmat {
  return {
    ...dto,
    cstics: dto.cstics ? dto.cstics : [],
  };
}

function transformGuidewayPieceLimits(dto: LinearConfigurationDTO | LinearConfigurationDTO, guidewayKmats: Kmat[]) {
  return Object.entries(dto.guidewayPieceLimits || {})
    .map(([subInstanceId, { minLength, maxLength }]) => {
      const index = guidewayKmats.findIndex((kmat) => kmat.id.split("-")[0] === subInstanceId);
      return [index, { min: minLength, max: maxLength }] as const;
    })
    .reduce(
      (map, [key, val]) => {
        map[key] = val;
        return map;
      },
      {} as Dict<{ min: number; max: number }>,
    );
}

function resolveSpecialTranslation(
  key: string,
  fieldName: "name" | "info" | "description" | "image",
  defaultValue: string,
  context: TransformationContext,
) {
  const val = context.specialTranslations[key]?.[fieldName];
  if (val) {
    return resolvePotentiallyExternalLabel(val, context);
  }
  return defaultValue;
}

function buildTranslationsMap(context: TransformationContext) {
  const translations: Record<string, string> = {};

  if (context.specialTranslations["VWAUBW_065"]) {
    translations[CARRIAGE_DETAILS_INFO_MSG] = transformToExternalUrl(
      context.specialTranslations["VWAUBW_065"].additionalTexts[0],
      context.baseExternalUrl,
    );
  }

  const isMediasUser = userIsRegistered() || userIsPublic();
  if (context.configuratorType === ConfiguratorType.System) {
    translations[DISCLAIMER_RAIL_BELOW] = GET_ORDER_DESIGNTION_TEXT(ORDER_DESIGNATION_HTML_IDS[isMediasUser ? 2 : 0]);
    translations[DISCLAIMER_RAIL_ABOVE] = GET_ORDER_DESIGNTION_TEXT(ORDER_DESIGNATION_HTML_IDS[isMediasUser ? 3 : 1]);
  }

  return translations;
}

function buildAdvancedTranslationsMap(
  context: TransformationContext,
  dto: LinearConfigurationDTO | LinearConfigurationDTO,
): Dict<TranslationModel> {
  let translations: Record<string, TranslationModel> = {};
  if (context.configuratorType === ConfiguratorType.System) {
    translations = buildAdvancedTranslationsForUnit(context, dto);
  }

  resolveInternalAdvancedTranslation(translations, RESET_SELECTION, "page.linear.resetStep.1");
  resolveInternalAdvancedTranslation(translations, RESET_STANDARD_AND_LENGTH, "page.linear.resetStep.2");
  resolveInternalAdvancedTranslation(translations, RESET_STANDARD, "page.linear.resetStep.3");
  resolveInternalAdvancedTranslation(translations, RESET_SELECTION_AND_LENGTH, "page.linear.resetStep.4");

  return translations;
}

function addMandatoryValuesForLubricationOptions(kmatMap: Dict<Kmat[]>, configuratorType: ConfiguratorType) {
  if (configuratorType === ConfiguratorType.System) {
    const kmat = getMainKmat({ kmats: kmatMap });
    pushStandardValueIfMissing(kmat, GREASING_KEY_DEFAULT_KMAT, GREASING_STANDARD_VALUE);
    pushStandardValueIfMissing(kmat, GREASING_OPTION_KEY_DEFAULT_KMAT, GREASING_OPTION_STANDARD_VALUE);
  }
}

const READ_ONLY_CSTICS = new Set([
  REFERENCE_MATERIAL_CSTIC,
  SHAFT_LENGTH_METRIC_MIN,
  SHAFT_LENGTH_INCH_MIN,
  SHAFT_LENGTH_INCH_MAX,
  SHAFT_LENGTH_METRIC_MAX,
  radialThread.aL.inch.min,
  radialThread.aL.inch.max,
  radialThread.aL.metric.min,
  radialThread.aL.metric.max,
  radialThread.aR.inch.min,
  radialThread.aR.inch.max,
  radialThread.aR.metric.min,
  radialThread.aR.metric.max,
  radialThread.pdt,
  radialThread.threadSpacing.inch,
  radialThread.threadSpacing.metric,
  radialThread.threadHoleAmount,
  radialThread.threadDepth,
  chamfer.left.width.nf,
  chamfer.left.width.minmax,
  chamfer.right.width.nf,
  chamfer.right.width.minmax,
]);
